import { useMemo, useSyncExternalStore } from 'react';
import { API_HOST } from '@/constants/config';
import publicNavigation from '../../shared/public/navigation.cjs';

const TTL_MS = 30_000;
const empty = { available: new Set(), loading: true, error: null };
let snapshot = empty;
let expires = 0;
let pending = null;
let timer;
const listeners = new Set();
const publish = value => { snapshot = value; listeners.forEach(listener=>listener()); };
const getSnapshot = () => snapshot;
const getServerSnapshot = () => empty;

export function refreshPublicNavigation() {
  if (pending) return pending;
  if (Date.now() < expires) return Promise.resolve(snapshot);
  // Expired data cannot keep an unpublished destination visible after failure.
  publish(empty);
  pending = Promise.resolve().then(async () => {
    try {
      const response = await fetch(`${API_HOST}/json/public-navigation`, { headers: { Accept: 'application/json', 'X-Platform': 'web' } });
      if (!response.ok || !(response.headers.get('content-type') || '').includes('application/json')) throw new Error('Navigation unavailable');
      const data = await response.json();
      if (data.schemaVersion !== 1 || !Array.isArray(data.available) || data.available.some(href=>typeof href!=='string')) throw new Error('Invalid navigation response');
      const allowed = new Set(publicNavigation.dynamicCandidates);
      publish({ available: new Set(data.available.filter(href=>allowed.has(href))), loading: false, error: null });
    } catch {
      publish({ available: new Set(), loading: false, error: 'Navigation unavailable' });
    } finally {
      expires = Date.now() + TTL_MS;
      pending = null;
    }
    return snapshot;
  });
  return pending;
}

function subscribe(listener) {
  listeners.add(listener);
  if (listeners.size === 1) {
    refreshPublicNavigation();
    timer = setInterval(refreshPublicNavigation, TTL_MS);
  }
  return () => {
    listeners.delete(listener);
    if (!listeners.size) { clearInterval(timer); timer = undefined; }
  };
}

export default function usePublicNavigation() {
  const state = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  return useMemo(()=>({ ...publicNavigation.projectNavigation(state.available), loading: state.loading, error: state.error }), [state]);
}
